"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { useLanguage } from "@/hooks/useLanguage";
import type { FavoriteTokenSummary } from "@/hooks/useFavoriteTokens";
import { cn } from "@/lib/utils";

interface FavoriteTokenListProps {
	favorites: FavoriteTokenSummary[];
	loading?: boolean;
	emptyText?: string;
	className?: string;
	itemClassName?: string;
}

export function FavoriteTokenList({
	favorites,
	loading = false,
	emptyText,
	className,
	itemClassName,
}: FavoriteTokenListProps) {
	const router = useRouter();
	const { getTranslation } = useLanguage();

	const formatPrice = (price: number) => `${(price / 1000).toLocaleString()} Sats`;

	return (
		<div className={cn("grid grid-cols-1 gap-2", className)}>
			{loading ? (
				<div className="space-y-2">
					{Array.from({ length: 3 }).map((_, i) => (
						<div
							key={i}
							className="flex items-center gap-2 rounded-xl bg-light-background p-3 dark:bg-dark-background"
						>
							<div className="h-8 w-8 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
							<div className="flex-1">
								<div className="mb-1 h-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
								<div className="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
							</div>
						</div>
					))}
				</div>
			) : favorites.length > 0 ? (
				favorites.map((token) => (
					<div
						key={token.id}
						className={cn(
							"cursor-pointer rounded-xl bg-light-background p-3 transition-shadow hover:shadow-md dark:bg-dark-background",
							"flex items-center gap-2",
							itemClassName,
						)}
						onClick={() => router.push(`/plaza/token/${token.id}`)}
					>
						<Image
							src={`https://images.odin.fun/token/${token.id}`}
							alt={token.ticker}
							width={32}
							height={32}
							className="rounded-full"
							onError={(e) => {
								(e.target as HTMLImageElement).style.display = "none";
							}}
						/>
						<div className="min-w-0 flex-1">
							<p className="truncate text-sm font-medium text-light-text dark:text-dark-text">
								{token.ticker}
							</p>
							<p className="text-xs text-light-accent dark:text-dark-accent">
								{formatPrice(token.price)}
							</p>
							<div className="mt-1 flex gap-3 text-xs text-gray-500">
								<span>
									{getTranslation("plaza.holders")}{" "}
									{(token.holder_count || 0).toLocaleString()}
								</span>
								<span>
									{getTranslation("plaza.comments")}{" "}
									{(token.comment_count || 0).toLocaleString()}
								</span>
							</div>
						</div>
					</div>
				))
			) : emptyText ? (
				<div className="py-4 text-center">
					<p className="text-sm text-gray-500">{emptyText}</p>
				</div>
			) : null}
		</div>
	);
}
